import { useMemo, useRef } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import notify from '../../utilities/notify'
import { captureErrorAndNotify, handleValidateAndNotify } from '../../utilities/errorHandlers'
import useMutation from '../../hooks/useMutation'
import useQuery from '../../hooks/useQuery'
import QUERY_KEYS from '../../graphql/queryKeys'
import termsAndConditionQuery from '../../graphql/queries/termsAndCondition'
import updateTermsAndConditionMutation from '../../graphql/mutations/updateTermsAndCondition'
import deleteTermsAndConditionMutation from '../../graphql/mutations/deleteTermsAndCondition'
import enableDisableTermsAndConditionMutation from '../../graphql/mutations/enableDisableTermsAndCondition'
import { ROUTES } from '../../routes'
import { AGREEMENT_STATUS } from '../create-terms-and-condition-agreement/constants'
import {
  mapFormToInput,
  mapApiToForm,
  isDraftAgreement,
  confirmAction
} from './utils'

export default function useEditTermsAndConditionAgreement () {
  const history = useHistory()
  const { id } = useParams()
  const { user } = useSelector(({ user }) => ({ user: user.user }))
  const isDirtyRef = useRef(false)

  const { data: { termsAndCondition: agreement } = {}, isFetching, isError } = useQuery(
    [QUERY_KEYS.termsAndCondition, { id, haulerId: user.haulerId }],
    termsAndConditionQuery,
    {
      enabled: Boolean(id && user.haulerId),
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve T&C agreement')
      }
    }
  )

  const initialValues = useMemo(
    () => (agreement ? mapApiToForm(agreement) : null),
    [agreement]
  )

  const isDraft = useMemo(() => isDraftAgreement(agreement), [agreement])
  const agreementStatus = agreement
    ? (agreement.isEnabled ? AGREEMENT_STATUS.ENABLED : AGREEMENT_STATUS.DISABLED)
    : AGREEMENT_STATUS.ENABLED

  const { mutate: updateTermsAndCondition, isLoading: isSaving } = useMutation(updateTermsAndConditionMutation, {
    onSuccess (_data, variables) {
      const savedAsDraft = !variables.input.publish
      notify(
        'success',
        savedAsDraft ? 'Agreement saved as draft' : 'Agreement updated successfully'
      )
      history.push(ROUTES.termsAndConditionsAgreements)
    },
    onError (error) {
      handleValidateAndNotify(error, 'Failed to update T&C agreement')
    }
  })

  const { mutate: deleteTermsAndCondition, isLoading: isDeleting } = useMutation(deleteTermsAndConditionMutation, {
    onSuccess () {
      notify('success', 'Agreement deleted successfully')
      history.push(ROUTES.termsAndConditionsAgreements)
    },
    onError (error) {
      captureErrorAndNotify(error, 'Failed to delete T&C agreement')
    }
  })

  const { mutate: enableDisableTermsAndCondition, isLoading: isTogglingStatus } = useMutation(
    enableDisableTermsAndConditionMutation,
    {
      onSuccess (_data, variables) {
        notify(
          'success',
          variables.isEnabled ? 'Agreement enabled successfully' : 'Agreement disabled successfully'
        )
        history.push(ROUTES.termsAndConditionsAgreements)
      },
      onError (error) {
        captureErrorAndNotify(error, 'Failed to update agreement status')
      }
    }
  )

  function saveAgreement (values, publish) {
    updateTermsAndCondition({
      id,
      input: mapFormToInput(values, publish, agreementStatus === AGREEMENT_STATUS.ENABLED)
    })
  }

  function handleStatusChange (nextStatus) {
    enableDisableTermsAndCondition({
      id,
      isEnabled: nextStatus === AGREEMENT_STATUS.ENABLED
    })
  }

  function destroyAgreement () {
    if (!confirmAction('THIS CANNOT BE UNDONE! Deleting this agreement will remove it permanently.')) return
    deleteTermsAndCondition({ id })
  }

  function handleBackClick () {
    if (isDirtyRef.current && !confirmAction('Are you sure you want to exit this page?')) return
    history.push(ROUTES.termsAndConditionsAgreements)
  }

  return {
    agreement,
    isFetching,
    isError,
    initialValues,
    isDraft,
    agreementStatus,
    isDirtyRef,
    isSaving,
    isDeleting,
    isTogglingStatus,
    saveAgreement,
    handleStatusChange,
    destroyAgreement,
    handleBackClick
  }
}
